"use client";

import { queryClient } from "@/core/caching";
import { logger } from "@/core/logging";
import { getRetrieveCurrentAssessmentQueryKey } from "@orval/ageless-assessments/ageless-assessments";
import type { AgelessAssessmentFlowType } from "@orval/model/agelessAssessmentFlowType";

import { assessmentSessionStorageService } from "./AssessmentSessionStorageService";
import { getAssessmentSessionQueryKey } from "./AssessmentSessionProvider";
import { clearNextStepsPath } from "./useNextStepsPath/useNextStepsPath";

export const resetAssessment = async (
  subdomainSlug: string,
  flowType: AgelessAssessmentFlowType,
) => {
  // Cleared before the session refetch in `clearAgelessAssessmentAuth`, which defines the new
  // assessment and stores its token and pending funnel start; clearing after would wipe both.
  assessmentSessionStorageService.clearAssessmentData();
  await clearAgelessAssessmentAuth(subdomainSlug, flowType);
  await clearNextStepsPath(subdomainSlug, flowType);

  logger.info("Resetting assessment", { subdomainSlug, flowType });

  return Promise.resolve();
};

export const clearAgelessAssessmentAuth = async (
  subdomainSlug: string,
  flowType: AgelessAssessmentFlowType,
) => {
  assessmentSessionStorageService.removeAuthToken({ subdomainSlug, flowType });

  const sessionKey = getAssessmentSessionQueryKey(subdomainSlug, flowType);
  await queryClient.invalidateQueries({ queryKey: sessionKey });

  await queryClient.resetQueries({ queryKey: sessionKey });
  await queryClient.removeQueries({
    queryKey: getRetrieveCurrentAssessmentQueryKey(),
  });

  await queryClient.refetchQueries({ queryKey: sessionKey });

  logger.info("Cleared ageless assessment auth", { subdomainSlug, flowType });

  return Promise.resolve();
};

export const setAgelessAssessmentAuth = (
  subdomainSlug: string,
  flowType: AgelessAssessmentFlowType,
  token: string,
) => {
  assessmentSessionStorageService.setAuthToken({ subdomainSlug, flowType }, token);
  logger.info("Set ageless assessment auth", { subdomainSlug, flowType });
};
